import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PlacesSearchBar from "../../components/PlacesSearchBar/PlacesSearchBar";
import { useLocalStorage } from "../../hooks/use-local-storage";
import useAlertModal from "../../hooks/useAlertModal";
import { usePlaceInfo } from "../../hooks/usePlaceInfo";
import { isValidEstablishment } from "../../lib/is-valid-establishment";
import { mapPlaceDocumentToPlace, Place } from "../../types/Place";
import PlaceDetails from "../PlaceDetails/PlaceDetails";
import { getPlaceIdFromUrl } from "./get-place-id-from-url";
import MapComponent from "./MapComponent";
import "./MapView.scss";
import { useBoundedPlaceQuery } from "./useBoundedPlaceQuery";

// TODO(gmike): Set a default center for a user based on their location/profile.
const DefaultCenter = { lat: 37.7749, lng: -122.4194 };
const DefaultZoom = 12;

const MapView: React.FC = () => {
  const { AlertModal, openModal: openAlertModal } = useAlertModal();

  const [savedCenter] = useLocalStorage<google.maps.LatLngLiteral>(
    "map-center",
    DefaultCenter
  );
  const [googlePlaceResult, setGooglePlaceResult] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [mapCenter, setMapCenter] =
    useState<google.maps.LatLngLiteral>(savedCenter);
  const [currentBounds, setCurrentBounds] =
    useState<google.maps.LatLngBounds | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const { places: boundedPlaces } = useBoundedPlaceQuery(currentBounds, 100);

  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(
    getPlaceIdFromUrl()
  );
  const [isPlaceCardVisible, setIsPlaceCardVisible] = useState<boolean>(false);
  const [userLocation, setUserLocation] =
    useState<google.maps.LatLngLiteral | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const { place: selectedPlace, loading: placeLoading } =
    usePlaceInfo(selectedPlaceId);

  useEffect(() => {
    const placeIdFromUrl = getPlaceIdFromUrl();
    if (placeIdFromUrl) {
      setSelectedPlaceId(placeIdFromUrl);
      setIsPlaceCardVisible(true);
    } else {
      setSelectedPlaceId(null);
      setIsPlaceCardVisible(false);
    }
  }, [location.hash]);

  useEffect(() => {
    if (selectedPlace && location.hash === `#${selectedPlace.googlePlaceId}`) {
      const boundedPlaceIds = boundedPlaces.map((place) => place.googlePlaceId);
      if (!boundedPlaceIds.includes(selectedPlace.googlePlaceId)) {
        setMapCenter({
          lat: selectedPlace.lat,
          lng: selectedPlace.lng,
        });
      }
    }
  }, [selectedPlace, boundedPlaces, location.hash]);

  const handleMapLoaded = useCallback((loadedMap: google.maps.Map) => {
    mapRef.current = loadedMap;
  }, []);

  const places: Place[] = useMemo(() => {
    return boundedPlaces.map(mapPlaceDocumentToPlace);
  }, [boundedPlaces]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const currentMap = mapRef.current;
    const listener = currentMap.addListener("idle", () => {
      const currentBounds = currentMap.getBounds();
      if (currentBounds) {
        setCurrentBounds(currentBounds);
      }
    });
    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, [mapRef]);

  const handleClosePlaceCard = useCallback(() => {
    setIsPlaceCardVisible(false);
    // Clear the hash by navigating to the current path without a hash
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname]);

  const handleClearGooglePlaceResult = useCallback(() => {
    setGooglePlaceResult(null);
  }, []);

  const handlePlaceSelected = useCallback(
    async (place: google.maps.places.PlaceResult) => {
      if (place.place_id && isValidEstablishment(place)) {
        setGooglePlaceResult(place);
        setSelectedPlaceId(place.place_id);
        setIsPlaceCardVisible(true);
      }

      setMapCenter({
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      });
    },
    []
  );

  const handleGeolocate = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const { latitude, longitude } = position.coords;
          const newUserLocation = { lat: latitude, lng: longitude };
          setUserLocation(newUserLocation);
        },
        (error: GeolocationPositionError) => {
          openAlertModal("Error getting location", error.message);
        }
      );
    } else {
      openAlertModal(
        "Geolocation not supported",
        "Your browser does not support geolocation."
      );
    }
  }, [openAlertModal]);

  return (
    <>
      <div className="map-search-bar-container">
        <PlacesSearchBar onPlaceSelected={handlePlaceSelected} />
        <div className="geolocation-button-container" onClick={handleGeolocate}>
          <button className="btn btn-primary btn-sm">
            <span className="material-symbols-outlined">location_on</span>
          </button>
        </div>
      </div>
      <MapComponent
        center={mapCenter}
        userLocation={userLocation}
        zoom={DefaultZoom}
        googlePlaceResult={googlePlaceResult}
        onClearGooglePlaceResult={handleClearGooglePlaceResult}
        highlightedPlaceId={selectedPlaceId}
        places={places}
        onMapLoad={handleMapLoaded}
      />
      {isPlaceCardVisible && selectedPlace && !placeLoading && (
        <PlaceDetails place={selectedPlace} onClose={handleClosePlaceCard} />
      )}
      <AlertModal />
    </>
  );
};

export default MapView;
